import { useState } from "react";
import { faqs, testimonials } from "@/mocks/articles";
import Breadcrumb from "@/components/feature/Breadcrumb";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/d9qagvfnahb7382ov8o0";

export default function About() {
  const [formStatus, setFormStatus] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: "关于我们" },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("phone_alt") as string;
    if (honeypot && honeypot.trim() !== "") {
      setFormStatus({
        type: "success",
        message: "消息已发送，我们会尽快回复！",
      });
      return;
    }

    formData.delete("phone_alt");

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });

      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: any;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }

      if (response.ok && parsed?.code === "OK") {
        setFormStatus({
          type: "success",
          message: "消息已发送，我们会尽快回复！",
        });
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message || parsed?.message || "提交失败，请稍后重试";
        setFormStatus({ type: "error", message: serverMsg });
      }
    } catch {
      setFormStatus({ type: "error", message: "网络错误，请检查网络后重试" });
    }
  };

  return (
    <>
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6 pt-6">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <section className="py-10 md:py-16 px-4 md:px-6">
        <div className="w-full max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="font-heading text-3xl md:text-4xl text-foreground-900 mb-4">
              关于我们
            </h1>
            <p className="text-sm text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              打屁股漫画精选合集，致力于为漫画爱好者提供最优质的打屁股漫画内容聚合平台
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">
            <div>
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-4">
                品牌故事
              </h2>
              <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                <p>
                  打屁股漫画精选合集创建于2024年，是国内最早专注于打屁股漫画内容聚合的平台之一。我们的创始团队由一群热爱漫画的年轻人组成，他们发现虽然打屁股漫画在日本和欧美拥有大量忠实读者，但在中文互联网上却缺乏一个系统、优质的资源整合平台。
                </p>
                <p>
                  于是，我们决定自己动手，打造一个集漫画推荐、分类导航、内容阅读于一体的综合性网站。从最初的手动收集整理，到如今涵盖搞笑、萌系、剧情、治愈、日常五大风格分类的丰富内容库，我们始终坚持一个信念：让每一位打屁股漫画爱好者都能在这里找到属于自己的快乐。
                </p>
                <p>
                  我们的编辑团队每天都会精心筛选和整理最新最优质的漫画资源，确保网站内容的持续更新。同时，我们也积极与原创漫画作者合作，为他们提供一个展示作品的平台，让更多优秀的打屁股漫画被读者看到。
                </p>
                <p>
                  无论是想放松心情的日常搞笑，还是想沉浸其中的精彩剧情，打屁股漫画精选合集都能满足你的需求。我们相信，好的漫画能给人带来温暖和快乐，而打屁股这个独特的主题，恰恰能以最纯粹的方式传达情感、制造欢笑。
                </p>
              </div>
            </div>

            <div className="w-full aspect-[4/3] rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Cozy%20reading%20corner%20with%20manga%20books%20and%20warm%20lighting%2C%20soft%20pastel%20colors%2C%20comfortable%20atmosphere%2C%20anime%20art%20style%20illustration%2C%20cream%20background%2C%20peaceful%20and%20inviting%20aesthetic%2C%20bookshelves%20filled%20with%20colorful%20comics%2C%20gentle%20sunlight%20through%20window&width=800&height=600&seq=about-brand-01&orientation=landscape"
                alt="打屁股漫画精选合集 - 品牌故事"
                title="打屁股漫画精选合集 - 品牌故事"
                className="w-full h-full object-cover object-top rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
        <div className="w-full max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
              读者口碑
            </h2>
            <p className="text-sm text-foreground-500">
              听听读者们怎么说
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {testimonials.map((t, index) => (
              <div
                key={index}
                className="bg-background-50 rounded-lg p-6 border border-background-200/70"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="font-heading text-sm text-foreground-900 whitespace-nowrap">
                    {t.name}
                  </span>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {t.comment}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 px-4 md:px-6">
        <div className="w-full max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500">
              有任何问题或建议？欢迎给我们留言
            </p>
          </div>

          <form
            id="about-contact-form"
            data-readdy-form="true"
            onSubmit={handleSubmit}
            className="bg-background-50 rounded-lg p-6 md:p-8 border border-background-200/70 space-y-5"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label
                  htmlFor="contact-name"
                  className="block text-sm font-medium text-foreground-700 mb-1.5"
                >
                  姓名
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
                  placeholder="请输入您的姓名"
                />
              </div>
              <div>
                <label
                  htmlFor="contact-email"
                  className="block text-sm font-medium text-foreground-700 mb-1.5"
                >
                  邮箱
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
                  placeholder="请输入您的邮箱"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="contact-subject"
                className="block text-sm font-medium text-foreground-700 mb-1.5"
              >
                主题
              </label>
              <input
                id="contact-subject"
                name="subject"
                type="text"
                required
                className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
                placeholder="请输入消息主题"
              />
            </div>

            <div>
              <label
                htmlFor="contact-message"
                className="block text-sm font-medium text-foreground-700 mb-1.5"
              >
                留言内容
              </label>
              <textarea
                id="contact-message"
                name="message"
                rows={5}
                maxLength={500}
                required
                className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors resize-none"
                placeholder="请输入您的留言内容（最多500字）"
              ></textarea>
              <p className="text-xs text-foreground-400 mt-1 text-right">
                最多500字
              </p>
            </div>

            <div className="spam-field">
              <label htmlFor="contact-phone-alt">备用电话</label>
              <input
                id="contact-phone-alt"
                name="phone_alt"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
            </div>

            {formStatus && (
              <div
                className={`p-4 rounded-lg text-sm ${
                  formStatus.type === "success"
                    ? "bg-accent-100 text-accent-700"
                    : "bg-primary-100 text-primary-700"
                }`}
              >
                {formStatus.message}
              </div>
            )}

            <button
              type="submit"
              className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap text-sm"
            >
              发送消息
            </button>
          </form>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div className="text-center p-5 bg-background-50 rounded-lg border border-background-200/70">
              <span className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-50 mb-3">
                <i className="ri-mail-line text-xl text-primary-500"></i>
              </span>
              <h3 className="font-heading text-sm text-foreground-900 mb-1">
                邮箱
              </h3>
              <p className="text-xs text-foreground-500">
                contact@spanking-comics.com
              </p>
            </div>
            <div className="text-center p-5 bg-background-50 rounded-lg border border-background-200/70">
              <span className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-accent-50 mb-3">
                <i className="ri-wechat-line text-xl text-accent-500"></i>
              </span>
              <h3 className="font-heading text-sm text-foreground-900 mb-1">
                微信
              </h3>
              <p className="text-xs text-foreground-500">
                SpankingComics
              </p>
            </div>
            <div className="text-center p-5 bg-background-50 rounded-lg border border-background-200/70">
              <span className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-secondary-50 mb-3">
                <i className="ri-time-line text-xl text-secondary-500"></i>
              </span>
              <h3 className="font-heading text-sm text-foreground-900 mb-1">
                工作时间
              </h3>
              <p className="text-xs text-foreground-500">
                周一至周五 9:00-18:00
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
        <div className="w-full max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
              常见问题
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden"
              >
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100/50 transition-colors list-none">
                  <span className="text-sm font-medium text-foreground-900 pr-4">
                    {faq.question}
                  </span>
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-400 group-open:rotate-180 transition-transform">
                    <i className="ri-arrow-down-s-line"></i>
                  </span>
                </summary>
                <div className="px-5 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "关于我们 - 打屁股漫画精选合集",
            description:
              "打屁股漫画精选合集，致力于为漫画爱好者提供最优质的打屁股漫画内容聚合平台。",
            url: "https://dda668.cn/about",
            isPartOf: {
              "@type": "WebSite",
              name: "打屁股漫画精选合集",
              url: "https://dda668.cn",
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer,
              },
            })),
          }),
        }}
      />
    </>
  );
}